import { useState } from 'react'
import { useMemo } from 'react'
import usePersistedFilters from '../../hooks/usePersistedFilters'
import useFetchData from '../../hooks/UseFetchData'
import Table from '../../components/his/invoices/Table'
import PageHeader from '../../shared/PageHeader'
import {
  FaArrowCircleLeft,
  FaArrowCircleRight,
  FaFileExcel,
  FaFilePdf
} from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import moment from 'moment'
import { useSelector } from 'react-redux'
import { ColumnGroup } from 'primereact/columngroup'
import { Row as ColumnRow } from 'primereact/row'
import * as XLSX from 'xlsx'
import { Column } from 'primereact/column'

import CustomButton from '../../shared/CustomButton'
import DropdownField from '../../shared/DropdownField'
import Filters from '../../components/his/invoices/Filters'
import { AmiriRegBase64 } from '../../assets/fonts/Amiri'
import { AmiriBoldBase64 } from '../../assets/fonts/AmiriB'
import {
  REPORT_MODES,
  reportModeOptions
} from '../../utils/UserCollectionReportModes'

const amount = value => Number.parseFloat(value || 0)
const money = value => amount(value).toFixed(2)

const userName = row => {
  if (
    row?.name &&
    row?.created_by &&
    String(row.name) === String(row.created_by)
  )
    return row.name
  if (row?.name && row?.created_by) return `${row.name} (${row.created_by})`
  return row?.name || row?.created_by || '-'
}

const getPaymentColumns = data => {
  const seen = new Set()
  const columns = []

  ;(data?.grouped_reports || []).forEach(section => {
    ;(section?.payment_groups || []).forEach(paymentGroup => {
      const key = paymentGroup?.payment_key || paymentGroup?.payment_method
      if (!key || seen.has(key)) return
      seen.add(key)
      columns.push({
        key,
        label:
          key === 'credit'
            ? 'Credit (Remaining)'
            : paymentGroup?.payment_method || key
      })
    })
  })

  return columns.sort((a, b) => {
    if (a.key === 'credit') return 1
    if (b.key === 'credit') return -1
    return 0
  })
}

const flattenDailyReports = (
  data,
  paymentColumns = getPaymentColumns(data)
) => {
  const rows = []
  const grandPaymentTotals = emptyPaymentTotals(paymentColumns)

  ;(data?.grouped_reports || []).forEach(section => {
    const sectionPaymentTotals = emptyPaymentTotals(paymentColumns)
    const users = {}
    const userOrder = []
    rows.push({
      isSectionHeader: true,
      label: section?.group_name
    })
    ;(section?.payment_groups || []).forEach(paymentGroup => {
      const paymentKey =
        paymentGroup?.payment_key || paymentGroup?.payment_method

      ;(paymentGroup?.rows || []).forEach(row => {
        const userKey = row?.created_by || row?.name || 'unknown'
        if (!users[userKey]) {
          users[userKey] = {
            ...row,
            payment_totals: emptyPaymentTotals(paymentColumns)
          }
          userOrder.push(userKey)
        }
        addPaymentTotals(
          users[userKey].payment_totals,
          paymentColumnTotals(paymentKey, row?.total_net)
        )
      })

      const paymentTotals = paymentColumnTotals(
        paymentKey,
        paymentGroup?.total?.total_net
      )
      addPaymentTotals(sectionPaymentTotals, paymentTotals)
      addPaymentTotals(grandPaymentTotals, paymentTotals)
    })

    userOrder.forEach(userKey => rows.push(users[userKey]))

    rows.push({
      ...(section?.total || {}),
      isSubtotal: true,
      isSectionTotal: true,
      label: `${section?.group_name} Total`,
      payment_totals: sectionPaymentTotals
    })
  })

  if (data?.grand_total) {
    rows.push({
      ...data.grand_total,
      isGrandTotal: true,
      label: 'Grand Total',
      payment_totals: grandPaymentTotals
    })
  }

  return rows
}

const flattenDailyUserBlocks = (data, paymentColumns = []) => {
  const blocks = []

  ;(data?.grouped_reports || []).forEach(section => {
    const users = {}
    const userOrder = []

    ;(section?.payment_groups || []).forEach(paymentGroup => {
      const paymentKey =
        paymentGroup?.payment_key || paymentGroup?.payment_method

      ;(paymentGroup?.rows || []).forEach(row => {
        const userKey = row?.created_by || row?.name || 'unknown'
        if (!users[userKey]) {
          users[userKey] = {
            ...row,
            payment_totals: emptyPaymentTotals(paymentColumns)
          }
          userOrder.push(userKey)
        }
        addPaymentTotals(
          users[userKey].payment_totals,
          paymentColumnTotals(paymentKey, row?.total_net, paymentColumns)
        )
      })
    })

    userOrder.forEach(userKey => {
      const user = users[userKey]
      blocks.push({
        sheetName: user?.name || user?.created_by || userKey,
        group_name: section?.group_name,
        rows: [
          { ...user, label: userName(user) },
          {
            ...(user?.payment_totals || {}),
            isSubtotal: true,
            isSectionTotal: true,
            label: 'Total',
            payment_totals: user?.payment_totals
          }
        ]
      })
    })
  })

  return blocks
}

const emptyPaymentTotals = (paymentColumns = []) => ({
  ...paymentColumns.reduce((acc, column) => ({ ...acc, [column.key]: 0 }), {}),
  overall_total: 0
})

const paymentColumnTotals = (paymentKey, value, paymentColumns = []) => {
  const totals = emptyPaymentTotals(paymentColumns)
  const total = amount(value)
  totals.overall_total = paymentKey === 'credit' ? 0 : total
  totals[paymentKey] = total

  return totals
}

const addPaymentTotals = (target, source) => {
  Object.keys(source || {}).forEach(key => {
    if (target[key] === undefined) target[key] = 0
    target[key] += amount(source?.[key])
  })
}

const dailyRowClassName = row => {
  if (row?.isSectionHeader) return 'user-collection-daily-section-row'
  if (row?.isGrandTotal) return 'user-collection-daily-grand-total-row'
  if (row?.isSubtotal) return 'user-collection-daily-subtotal-row'
  return ''
}

const paymentCell = field => row => {
  if (row?.isSectionHeader) return ''
  return (
    <span className={row?.isSubtotal || row?.isGrandTotal ? 'font-bold' : ''}>
      {money(row?.payment_totals?.[field])}
    </span>
  )
}

const splitPaymentColumns = paymentColumns => ({
  receivedColumns: paymentColumns.filter(column => column.key !== 'credit'),
  remainingColumns: paymentColumns.filter(column => column.key === 'credit')
})

const columns = (t, paymentColumns = []) => [
  {
    header: t('Employee'),
    bodyCellClass: 'border !border-gray-300',
    body: row => {
      if (row?.isSectionHeader) {
        return <span className='font-bold uppercase'>{t(row?.label)}</span>
      }
      if (row?.isSubtotal || row?.isGrandTotal) {
        return <span className='font-bold'>{t(row?.label || row?.name)}</span>
      }
      return userName(row)
    },
    headerCellClass: '!bg-gray-200'
  },
  ...splitPaymentColumns(paymentColumns).receivedColumns.map(column => ({
    header: t(column.label),
    bodyCellClass: 'border !border-gray-300',
    body: paymentCell(column.key),
    headerCellClass: '!bg-gray-200'
  })),
  {
    header: t('Total Payment'),
    bodyCellClass: 'border !border-gray-300',
    body: paymentCell('overall_total'),
    headerCellClass: '!bg-gray-200'
  },
  ...splitPaymentColumns(paymentColumns).remainingColumns.map(column => ({
    header: t(column.label),
    bodyCellClass: 'border !border-gray-300',
    body: paymentCell(column.key),
    headerCellClass: '!bg-gray-200'
  }))
]

const buildDailyWorksheet = (data, t, paymentColumns = []) => {
  const { receivedColumns, remainingColumns } =
    splitPaymentColumns(paymentColumns)
  const formatted = data?.map(row => {
    const isHeader = row?.isSectionHeader
    const isTotal = row?.isSubtotal || row?.isGrandTotal
    return {
      [t('Employee')]:
        isHeader || isTotal ? t(row?.label || row?.name) : userName(row),
      ...receivedColumns.reduce(
        (acc, column) => ({
          ...acc,
          [t(column.label)]: isHeader
            ? ''
            : money(row?.payment_totals?.[column.key])
        }),
        {}
      ),
      [t('Total Payment')]: isHeader
        ? ''
        : money(row?.payment_totals?.overall_total),
      ...remainingColumns.reduce(
        (acc, column) => ({
          ...acc,
          [t(column.label)]: isHeader
            ? ''
            : money(row?.payment_totals?.[column.key])
        }),
        {}
      )
    }
  })

  const worksheet = XLSX.utils.json_to_sheet(formatted)
  worksheet['!cols'] = new Array(Object.keys(formatted[0]).length).fill({
    wch: 20
  })
  worksheet['!rows'] = new Array(formatted.length).fill({ hpt: 22 })

  return worksheet
}

export const exportInvoicesToExcel = (
  data,
  t,
  paymentColumns = [],
  fileName = 'user-collection-daily-report.xlsx'
) => {
  const workbook = XLSX.utils.book_new()
  XLSX.utils.book_append_sheet(
    workbook,
    buildDailyWorksheet(data, t, paymentColumns),
    'User Collection Daily Report'
  )
  XLSX.writeFile(workbook, fileName)
}

export const exportInvoicesIndividualToExcel = (
  blocks,
  combinedRows,
  t,
  paymentColumns = [],
  fileName = 'user-collection-daily-report.xlsx'
) => {
  const workbook = XLSX.utils.book_new()
  ;(blocks || []).forEach(block => {
    const sheetName = block.sheetName?.slice?.(0, 31) || 'Individual'
    XLSX.utils.book_append_sheet(
      workbook,
      buildDailyWorksheet(block.rows, t, paymentColumns),
      sheetName
    )
  })
  if (combinedRows?.length) {
    XLSX.utils.book_append_sheet(
      workbook,
      buildDailyWorksheet(combinedRows, t, paymentColumns),
      'Combined'
    )
  }
  XLSX.writeFile(workbook, fileName)
}

const drawDailyPdfHeader = (doc, t, payloadData) => {
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 14
  const currentDate = moment().format('DD/MM/YYYY HH:mm')
  const start_date = moment(payloadData?.start_date).format('DD/MM/YYYY')
  const end_date = moment(payloadData?.end_date).format('DD/MM/YYYY')
  const user_name = payloadData?.user_name || t('All Users')
  const reportTitle = t('Daily Collections Report')
  const orgName =
    payloadData?.business_en || payloadData?.business_ar || t('Organization')
  const location = payloadData?.address || ''

  const drawHeader = () => {
    doc.setFillColor(55, 65, 81)
    doc.rect(0, 0, pageWidth, 28, 'F')
    if (payloadData?.logo) {
      try {
        doc.addImage(
          `data:image/png;base64,${payloadData.logo}`,
          'PNG',
          margin,
          3,
          20,
          20
        )
      } catch (_) {}
    }
    doc.setTextColor(255, 255, 255)
    doc.setFontSize(11)
    doc.setFont('Amiri', 'bold')
    doc.text(orgName, margin + 24, 11)
    doc.setFontSize(8)
    doc.setFont('Amiri', 'normal')
    if (location) doc.text(location, margin + 24, 17)
    doc.setFontSize(12)
    doc.setFont('Amiri', 'bold')
    doc.text(reportTitle, pageWidth - margin, 11, { align: 'right' })
    doc.setFontSize(8)
    doc.setFont('Amiri', 'normal')
    doc.text(`${t('Printed On')}: ${currentDate}`, pageWidth - margin, 17, {
      align: 'right'
    })
  }

  drawHeader()

  let y = 32
  doc.setTextColor(50, 50, 50)
  doc.setFontSize(8)
  doc.setFont('Amiri', 'normal')
  doc.text(`${t('Period')}: ${start_date} - ${end_date}`, margin, y)
  doc.text(`${t('Report For')}: ${user_name}`, pageWidth - margin, y, {
    align: 'right'
  })
  doc.setDrawColor(200, 200, 200)
  doc.setLineWidth(0.3)
  doc.line(margin, y + 3, pageWidth - margin, y + 3)
  y += 8

  return { y, drawHeader }
}

const buildDailyPdfRows = (payload, t, receivedColumns, remainingColumns) =>
  payload.map(row => {
    const isHeader = row?.isSectionHeader
    const isTotal = row?.isSubtotal || row?.isGrandTotal
    const style = { fontStyle: isHeader || isTotal ? 'bold' : 'normal' }
    return [
      {
        content:
          isHeader || isTotal ? t(row?.label || row?.name) : userName(row),
        styles: { ...style, halign: 'left' }
      },
      ...[
        ...receivedColumns.map(column => column.key),
        'overall_total',
        ...remainingColumns.map(column => column.key)
      ].map(field => ({
        content: isHeader ? '' : money(row?.payment_totals?.[field]),
        styles: { ...style, halign: 'right' }
      }))
    ]
  })

const drawDailyPdfTable = (
  doc,
  payload,
  t,
  receivedColumns,
  remainingColumns,
  startY,
  pageHeight,
  drawHeader
) => {
  const pageWidth = doc.internal.pageSize.getWidth()
  const margin = 14
  const tableHeaders = [
    [
      t('Employee'),
      ...receivedColumns.map(column => t(column.label)),
      t('Total Payment'),
      ...remainingColumns.map(column => t(column.label))
    ]
  ]

  autoTable(doc, {
    startY,
    head: tableHeaders,
    body: buildDailyPdfRows(
      payload,
      t,
      receivedColumns,
      remainingColumns
    ),
    theme: 'grid',
    margin: { left: margin, right: margin, top: 28 },
    styles: {
      fontSize: 10,
      cellPadding: { top: 2.5, right: 3, bottom: 2.5, left: 3 },
      lineColor: [210, 210, 210],
      lineWidth: 0.2,
      font: 'Amiri',
      overflow: 'linebreak',
      textColor: [30, 30, 30]
    },
    headStyles: {
      fillColor: [209, 213, 219],
      textColor: [17, 24, 39],
      fontStyle: 'bold',
      halign: 'center',
      font: 'Amiri',
      fontSize: 10,
      valign: 'middle'
    },
    alternateRowStyles: { fillColor: [245, 247, 250] },
    bodyStyles: { fillColor: [255, 255, 255], font: 'Amiri', fontSize: 10 },
    showHead: 'everyPage',
    willDrawCell: data => {
      const row = payload[data.row.index]
      if (row?.isGrandTotal) {
        data.cell.styles.fillColor = [55, 65, 81]
        data.cell.styles.textColor = [255, 255, 255]
        data.cell.styles.fontStyle = 'bold'
      } else if (row?.isSubtotal) {
        data.cell.styles.fillColor = [229, 231, 235]
        data.cell.styles.fontStyle = 'bold'
      } else if (row?.isSectionHeader) {
        data.cell.styles.fontStyle = 'bold'
      }
    },
    didDrawPage: () => {
      drawHeader()
      const pageCount = doc.internal.getNumberOfPages()
      doc.setFontSize(7)
      doc.setTextColor(130, 130, 130)
      doc.text(
        `${t('Page')} ${doc.internal.getCurrentPageInfo().pageNumber} ${t(
          'of'
        )} ${pageCount}`,
        pageWidth / 2,
        pageHeight - 6,
        { align: 'center' }
      )
    }
  })

  return doc.lastAutoTable.finalY
}

const createDailyPdfDoc = () => {
  const doc = new jsPDF('portrait')
  doc.addFileToVFS('Amiri-Regular.ttf', AmiriRegBase64)
  doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal')
  doc.addFileToVFS('Amiri-Bold.ttf', AmiriBoldBase64)
  doc.addFont('Amiri-Bold.ttf', 'Amiri', 'bold')
  doc.setFont('Amiri', 'normal')
  return doc
}

export const exportInvoicesToPDF = (
  payload,
  t,
  payloadData,
  paymentColumns = [],
  fileName = `user-collection-daily-report-${moment().unix()}.pdf`
) => {
  const { receivedColumns, remainingColumns } =
    splitPaymentColumns(paymentColumns)
  const doc = createDailyPdfDoc()
  const pageHeight = doc.internal.pageSize.getHeight()
  const { y, drawHeader } = drawDailyPdfHeader(doc, t, payloadData)

  drawDailyPdfTable(
    doc,
    payload,
    t,
    receivedColumns,
    remainingColumns,
    y,
    pageHeight,
    drawHeader
  )

  doc.save(fileName)
}

export const exportInvoicesIndividualToPDF = (
  blocks,
  combinedRows,
  t,
  payloadData,
  paymentColumns = [],
  fileName = `user-collection-daily-report-${moment().unix()}.pdf`
) => {
  const { receivedColumns, remainingColumns } =
    splitPaymentColumns(paymentColumns)
  const doc = createDailyPdfDoc()
  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 14
  const { y, drawHeader } = drawDailyPdfHeader(doc, t, payloadData)

  let startY = y

  ;(blocks || []).forEach(block => {
    doc.setFontSize(12)
    doc.setFont('Amiri', 'bold')
    doc.setTextColor(30, 41, 59)
    doc.text(block.sheetName, margin, startY)
    startY += 6
    startY = drawDailyPdfTable(
      doc,
      block.rows,
      t,
      receivedColumns,
      remainingColumns,
      startY,
      pageHeight,
      drawHeader
    )
    startY += 6
    doc.setDrawColor(200, 200, 200)
    doc.setLineWidth(0.2)
    doc.line(margin, startY, pageWidth - margin, startY)
    startY += 4
  })

  if (combinedRows?.length) {
    doc.setFontSize(12)
    doc.setFont('Amiri', 'bold')
    doc.setTextColor(30, 41, 59)
    doc.text(t('Combined'), margin, startY)
    startY += 6
    drawDailyPdfTable(
      doc,
      combinedRows,
      t,
      receivedColumns,
      remainingColumns,
      startY,
      pageHeight,
      drawHeader
    )
  }

  doc.save(fileName)
}

const UserCollectionDailyReport = () => {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const {
    data: requestData,
    executeFetch: requestFetch,
    loading
  } = useFetchData()
  const auth = useSelector(state => state.auth)
  const { filters: payloadData, setFilters: setPayloadData } =
    usePersistedFilters('user_collection_daily_report', {
      end_date: moment().toDate(),
      start_date: moment().subtract(1, 'day').toDate(),
      created_by: '',
      cash_invoice_number: '',
      department_id: '',
      report_display_mode: REPORT_MODES.individual
    })

  const requestReport = async (payload = payloadData) => {
    await requestFetch('POST', '/v3/reports/user-collection-grouped-daily', {
      start_date: moment(payload.start_date).format('YYYY-MM-DD'),
      end_date: moment(payload.end_date).format('YYYY-MM-DD'),
      created_by: payload.created_by || ''
    })
  }
  const paymentColumns = getPaymentColumns(requestData)
  const { receivedColumns, remainingColumns } =
    splitPaymentColumns(paymentColumns)
  const dailyRows = flattenDailyReports(requestData, paymentColumns)
  const userBlocks = useMemo(
    () => flattenDailyUserBlocks(requestData, paymentColumns),
    [requestData, paymentColumns]
  )
  const displayMode =
    payloadData?.report_display_mode || REPORT_MODES.combined

  const handleModeChange = event => {
    const value = event?.target?.value ?? event?.value
    setPayloadData(prev => ({ ...prev, report_display_mode: value }))
  }

  const headerGroup = (
    <ColumnGroup>
      <ColumnRow>
        <Column header={t('Employee')} style={{ paddingLeft: '0.5rem' }} />
        {receivedColumns.map(column => (
          <Column
            key={column.key}
            header={t(column.label)}
            style={{ paddingLeft: '0.5rem' }}
          />
        ))}
        <Column header={t('Total Payment')} style={{ paddingLeft: '0.5rem' }} />
        {remainingColumns.map(column => (
          <Column
            key={column.key}
            header={t(column.label)}
            style={{ paddingLeft: '0.5rem' }}
          />
        ))}
      </ColumnRow>
    </ColumnGroup>
  )

  return (
    <div>
      <div>
        <div className='flex items-center w-full justify-between gap-2 mb-2'>
          <Filters
            payloadData={payloadData}
            setPayloadData={setPayloadData}
            requestReport={requestReport}
            responseReport={() => {}}
            showDepartment={false}
            showCashInvoiceNumber={false}
            maxRangeDays={31}
          />
          <div className='flex gap-2 items-center'>
            <DropdownField
              placeholder={t('Show Reports')}
              className='!mb-0 !w-[180px] !h-[35px] justify-center'
              name='report_display_mode'
              useFormik={false}
              options={reportModeOptions(t)}
              optionLabel='label'
              optionValue='value'
              value={displayMode}
              onChange={handleModeChange}
            />
            <CustomButton
              //label={t('Export to Excel')}
              title={t('Export to Excel')}
              buttonType='success'
              icon={FaFileExcel}
              className='w-fit h-[35px]'
              onClick={() => {
                displayMode === REPORT_MODES.combined
                  ? exportInvoicesToExcel(dailyRows, t, paymentColumns)
                  : exportInvoicesIndividualToExcel(
                      userBlocks,
                      displayMode === REPORT_MODES.both ? dailyRows : [],
                      t,
                      paymentColumns
                    )
              }}
            />

            <CustomButton
              //label={t('Export to PDF')}
              title={t('Export to PDF')}
              buttonType='danger'
              icon={FaFilePdf}
              className='w-fit h-[35px]'
              onClick={() => {
                const pdfPayloadData = {
                  ...payloadData,
                  business_en: auth?.buisnessInfo?.name_en,
                  business_ar: auth?.buisnessInfo?.name_ar,
                  address: auth?.buisnessInfo?.address,
                  logo: auth?.buisnessInfo?.logo
                }
                displayMode === REPORT_MODES.combined
                  ? exportInvoicesToPDF(
                      dailyRows,
                      t,
                      pdfPayloadData,
                      paymentColumns
                    )
                  : exportInvoicesIndividualToPDF(
                      userBlocks,
                      displayMode === REPORT_MODES.both ? dailyRows : [],
                      t,
                      pdfPayloadData,
                      paymentColumns
                    )
              }}
            />
          </div>
        </div>

        {displayMode === REPORT_MODES.combined ? (
          <Table
            data={dailyRows}
            loading={loading}
            columns={t => columns(t, paymentColumns)}
            headerColumnGroup={headerGroup}
            showGridlines
            rowClassName={dailyRowClassName}
            rootyStyles='overflow-auto border border-gray-200 rounded border-b-0 custom-table user-collection-daily-table'
          />
        ) : (
          <div className='space-y-4'>
            {userBlocks.map((block, index) => (
              <div key={index}>
                <div className='bg-gray-300 text-gray-800 font-bold text-[14px] uppercase tracking-wide px-4 py-2 rounded-t'>
                  {block.sheetName}
                </div>
                <Table
                  data={block.rows}
                  loading={loading}
                  columns={t => columns(t, paymentColumns)}
                  headerColumnGroup={headerGroup}
                  showGridlines
                  rowClassName={dailyRowClassName}
                  rootyStyles='overflow-auto border border-gray-200 rounded border-b-0 custom-table user-collection-daily-table'
                />
              </div>
            ))}
            {displayMode === REPORT_MODES.both && dailyRows?.length > 0 && (
              <div>
                <div className='bg-gray-300 text-gray-800 font-bold text-[14px] uppercase tracking-wide px-4 py-2 rounded-t'>
                  {t('Combined')}
                </div>
                <Table
                  data={dailyRows}
                  loading={loading}
                  columns={t => columns(t, paymentColumns)}
                  headerColumnGroup={headerGroup}
                  showGridlines
                  rowClassName={dailyRowClassName}
                  rootyStyles='overflow-auto border border-gray-200 rounded border-b-0 custom-table user-collection-daily-table'
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default UserCollectionDailyReport
